import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Calendar,
  User,
  Building,
  Eye,
  Download,
  RefreshCw,
  ArrowUpDown,
  CheckCircle2,
  AlertTriangle,
  Users,
  PlusCircle,
  PencilLine,
  Trash2,
  LogIn,
  LogOut,
  Activity
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { workspaceAdminAPI } from '../../services/api';
import { useTableControls } from '../../hooks/useTableControls';
import { TableControls } from '../ui/table-controls';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import { EmptyState, StatSkeletonGrid, CardSkeleton } from '../common/States';
import { StatCard } from '../common/StatCard';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Client-side segment selected by clicking a KPI card: null | 'success' | 'issue' | 'actors'.
  const [outcomeFilter, setOutcomeFilter] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    user_id: '',
    tenant_id: '',
    start_date: '',
    end_date: ''
  });

  const buildQueryParams = useCallback(() => {
    const params = {};

    const action = String(filters.action || '').trim();
    if (action) params.action = action;

    const tenantIdRaw = String(filters.tenant_id || '').trim();
    if (tenantIdRaw) {
      const parsed = Number.parseInt(tenantIdRaw, 10);
      if (Number.isFinite(parsed)) params.tenant_id = parsed;
    }

    const actorEmail = String(filters.actor_email || '').trim();
    if (actorEmail) params.actor_email = actorEmail;

    return params;
  }, [filters.action, filters.actor_email, filters.tenant_id]);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await workspaceAdminAPI.getAuditLogs(buildQueryParams());
      setLogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error(getErrorMessage(error, 'Failed to load audit logs'));
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleExport = async () => {
    try {
      const response = await workspaceAdminAPI.exportAuditLogs(filters);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getActionBadgeVariant = (action) => {
    const normalized = (action || '').toLowerCase();
    if (normalized.includes('create')) return 'default';
    if (normalized.includes('update')) return 'secondary';
    if (normalized.includes('delete')) return 'destructive';
    if (normalized.includes('login') || normalized.includes('logout')) return 'outline';
    return 'secondary';
  };

  const getActionMeta = (action) => {
    const normalized = (action || '').toLowerCase();
    if (normalized.includes('create')) return { Icon: PlusCircle, tone: 'bg-success/10 text-success' };
    if (normalized.includes('update')) return { Icon: PencilLine, tone: 'bg-info/10 text-info' };
    if (normalized.includes('delete')) return { Icon: Trash2, tone: 'bg-danger/10 text-danger' };
    if (normalized.includes('login')) return { Icon: LogIn, tone: 'bg-primary/10 text-primary' };
    if (normalized.includes('logout')) return { Icon: LogOut, tone: 'bg-muted text-muted-foreground' };
    return { Icon: Activity, tone: 'bg-muted text-muted-foreground' };
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    if (!Number.isFinite(diffMs)) return '';
    const sec = Math.round(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 30) return `${day}d ago`;
    const mon = Math.round(day / 30);
    if (mon < 12) return `${mon}mo ago`;
    return `${Math.round(mon / 12)}y ago`;
  };

  const formatDetailLabel = useCallback((key) => {
    const normalized = String(key || '').replace(/[_-]+/g, ' ').trim();
    if (!normalized) return 'Detail';
    return normalized
      .split(/\s+/)
      .map((part) => {
        const lower = part.toLowerCase();
        if (lower === 'id' || lower === 'ip' || lower === 'api') return lower.toUpperCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }, []);

  const formatDetailValue = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
      return value
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => formatDetailValue(item))
        .filter(Boolean)
        .join(', ');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([entryKey, entryValue]) => {
          const nestedValue = formatDetailValue(entryValue);
          if (!nestedValue) return '';
          return `${formatDetailLabel(entryKey)}: ${nestedValue}`;
        })
        .filter(Boolean)
        .join(', ');
    }
    return String(value);
  }, [formatDetailLabel]);

  const toDisplayText = useCallback((value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }, []);

  const getDetailsText = useCallback((details) => {
    if (!details) return '';
    if (typeof details === 'string') {
      const trimmed = details.trim();
      if (!trimmed) return '';
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return getDetailsText(JSON.parse(trimmed));
        } catch (error) {
          return trimmed;
        }
      }
      return trimmed;
    }
    if (Array.isArray(details)) {
      return details.map((item) => getDetailsText(item)).filter(Boolean).join(' | ');
    }
    if (typeof details === 'object') {
      return Object.entries(details)
        .map(([key, value]) => {
          const formattedValue = formatDetailValue(value);
          if (!formattedValue) return '';
          return `${formatDetailLabel(key)}: ${formattedValue}`;
        })
        .filter(Boolean)
        .join(' | ');
    }
    return toDisplayText(details);
  }, [formatDetailLabel, formatDetailValue, toDisplayText]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log =>
      toDisplayText(log.action).toLowerCase().includes(searchTerm.toLowerCase()) ||
      toDisplayText(log.target).toLowerCase().includes(searchTerm.toLowerCase()) ||
      toDisplayText(log.actor_email).toLowerCase().includes(searchTerm.toLowerCase()) ||
      toDisplayText(log.tenant_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      getDetailsText(log.details).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [logs, searchTerm, getDetailsText, toDisplayText]);

  // Apply the KPI-card segment on top of the search filter for the table only, so the
  // card counts themselves stay stable.
  const displayedLogs = useMemo(() => {
    if (!outcomeFilter) return filteredLogs;
    if (outcomeFilter === 'success') return filteredLogs.filter((log) => log.outcome === 'success');
    if (outcomeFilter === 'issue') return filteredLogs.filter((log) => log.outcome && log.outcome !== 'success');
    if (outcomeFilter === 'actors') return filteredLogs.filter((log) => Boolean(log.actor_email));
    return filteredLogs;
  }, [filteredLogs, outcomeFilter]);

  const table = useTableControls(displayedLogs, {
    initialSortKey: 'created_at',
    initialSortDirection: 'desc',
  });

  const kpis = useMemo(() => {
    const total = filteredLogs.length;
    const successful = filteredLogs.filter((log) => log.outcome === 'success').length;
    const warningOrFailed = filteredLogs.filter((log) => log.outcome && log.outcome !== 'success').length;
    const uniqueActors = new Set(filteredLogs.map((log) => log.actor_email).filter(Boolean)).size;
    return { total, successful, warningOrFailed, uniqueActors };
  }, [filteredLogs]);

  // Clicking a KPI card filters the table below; clicking the active card (or Total) clears it.
  const toggleSegment = (segment) => setOutcomeFilter((current) => (current === segment ? null : segment));
  const segmentClass = (segment) => (outcomeFilter === segment ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : '');

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const renderSortableHeader = (label, key) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-medium hover:text-primary"
      onClick={() => table.onSort(key)}
    >
      {label}
      <ArrowUpDown className="h-3.5 w-3.5" />
    </button>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <StatSkeletonGrid count={4} />
        <CardSkeleton rows={3} />
        <CardSkeleton rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Audit Logs</h2>
          <p className="text-muted-foreground">Monitor all system activities and changes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={loadLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Summary — click a card to filter the log below */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Events" value={kpis.total} icon={Activity} tone="primary"
          hint={outcomeFilter ? 'Show all' : undefined}
          className={!outcomeFilter ? segmentClass(null) : ''}
          onClick={() => setOutcomeFilter(null)}
        />
        <StatCard
          label="Successful" value={kpis.successful} icon={CheckCircle2} tone="success"
          className={segmentClass('success')}
          onClick={() => toggleSegment('success')}
        />
        <StatCard
          label="Warnings/Failed" value={kpis.warningOrFailed} icon={AlertTriangle} tone="warning"
          className={segmentClass('issue')}
          onClick={() => toggleSegment('issue')}
        />
        <StatCard
          label="Unique Actors" value={kpis.uniqueActors} icon={Users} tone="info"
          className={segmentClass('actors')}
          onClick={() => toggleSegment('actors')}
        />
      </div>

      {/* Active-segment banner */}
      {outcomeFilter && (
        <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm">
          <span className="text-muted-foreground">
            Showing <span className="font-medium text-foreground">{displayedLogs.length}</span> of {filteredLogs.length} events
            {outcomeFilter === 'success' && ' · Successful'}
            {outcomeFilter === 'issue' && ' · Warnings/Failed'}
            {outcomeFilter === 'actors' && ' · With a known actor'}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setOutcomeFilter(null)}>Clear</Button>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                Filter
              </Button>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Action Type</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                  <option value="read">Read</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Apply Filters</label>
                <Button onClick={loadLogs} className="w-full">
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        {/* Sort bar */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-3 text-sm text-muted-foreground">
          <span className="text-hr-xs font-medium uppercase tracking-wide">Sort by</span>
          {renderSortableHeader('Time', 'created_at')}
          {renderSortableHeader('Action', 'action')}
          {renderSortableHeader('User', 'actor_email')}
          {renderSortableHeader('Organization', 'tenant_id')}
          {renderSortableHeader('Target', 'target')}
          {renderSortableHeader('IP Address', 'client_ip')}
        </div>
        <CardContent className="p-0">
          {displayedLogs.length === 0 && !loading ? (
            <EmptyState
              icon={Eye}
              title="No audit events"
              description="Try adjusting your search criteria or filters."
            />
          ) : (
            <ol className="relative divide-y divide-border">
              {table.data.map((log) => {
                const { Icon, tone } = getActionMeta(log.action);
                return (
                  <li key={log.id} className="card-interactive relative flex gap-4 p-4 sm:p-5 hover:bg-muted/40">
                    {/* Action icon */}
                    <div className={`mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl ${tone}`}>
                      <Icon className="h-[18px] w-[18px]" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Badge variant={getActionBadgeVariant(log.action)}>
                          {log.action}
                        </Badge>
                        <span className="font-medium text-foreground">
                          {log.actor_email || 'Unknown'}
                        </span>
                        <span className="text-muted-foreground">·</span>
                        <span
                          className="inline-flex items-center gap-1 text-hr-xs text-muted-foreground"
                          title={formatDateTime(log.created_at)}
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          {formatRelativeTime(log.created_at)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-hr-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Building className="h-3.5 w-3.5" />
                          {log.tenant_id ? `Tenant #${log.tenant_id}` : 'System'}
                        </span>
                        <span className="inline-flex items-center gap-1 truncate">
                          <User className="h-3.5 w-3.5" />
                          {log.target || log.request_path || 'N/A'}
                        </span>
                        <span className="font-mono text-muted-foreground">
                          {log.client_ip || 'N/A'}
                        </span>
                      </div>
                      {log.details ? (
                        <p className="line-clamp-2 text-sm text-foreground/80">
                          {getDetailsText(log.details)}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">No details</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
        {filteredLogs.length > 0 && (
          <TableControls
            totalRows={table.totalRows}
            page={table.page}
            totalPages={table.totalPages}
            pageSize={table.pageSize}
            pageSizes={table.pageSizes}
            onPageSizeChange={table.onPageSizeChange}
            onPrevious={table.goToPreviousPage}
            onNext={table.goToNextPage}
            exportConfig={{
              title: 'Audit Logs',
              fileName: `audit-logs-${new Date().toISOString().split('T')[0]}`,
              rows: filteredLogs,
              columns: [
                { label: 'Timestamp', accessor: (row) => formatDateTime(row.created_at) },
                { label: 'Action', accessor: (row) => row.action || '' },
                { label: 'User', accessor: (row) => row.actor_email || 'Unknown' },
                { label: 'Organization', accessor: (row) => (row.tenant_id ? `Tenant #${row.tenant_id}` : 'System') },
                { label: 'Target', accessor: (row) => row.target || row.request_path || 'N/A' },
                { label: 'Details', accessor: (row) => getDetailsText(row.details) || '' },
                { label: 'IP Address', accessor: (row) => row.client_ip || 'N/A' },
              ],
            }}
          />
        )}
      </Card>

      <div className="text-hr-xs text-muted-foreground">
        Showing {Math.min(table.page * table.pageSize, table.totalRows)} of {table.totalRows} filtered logs
      </div>
    </div>
  );
};

export default AuditLogs;
